import {
  Add as AddIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  Code as CodeIcon,
  Computer as ComputerIcon,
  Dashboard as DashboardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  Folder as FolderIcon,
  GetApp as GetAppIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Layers as LayersIcon,
  Lock as LockIcon,
  Memory as MemoryIcon,
  NetworkCheck as NetworkCheckIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
  Storage as StorageIcon,
  SyncAlt as SyncAltIcon,
  Terminal as TerminalIcon,
  Update as UpdateIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Wifi as WifiIcon,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useState } from 'react';

interface IconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
  label?: string;
  helperText?: string;
}

const AVAILABLE_ICONS = [
  { name: 'Add', component: AddIcon },
  { name: 'Build', component: BuildIcon },
  { name: 'BugReport', component: BugReportIcon },
  { name: 'Code', component: CodeIcon },
  { name: 'Computer', component: ComputerIcon },
  { name: 'Dashboard', component: DashboardIcon },
  { name: 'Delete', component: DeleteIcon },
  { name: 'Edit', component: EditIcon },
  { name: 'Error', component: ErrorIcon },
  { name: 'Folder', component: FolderIcon },
  { name: 'GetApp', component: GetAppIcon },
  { name: 'Home', component: HomeIcon },
  { name: 'Info', component: InfoIcon },
  { name: 'Layers', component: LayersIcon },
  { name: 'Lock', component: LockIcon },
  { name: 'Memory', component: MemoryIcon },
  { name: 'NetworkCheck', component: NetworkCheckIcon },
  { name: 'Notifications', component: NotificationsIcon },
  { name: 'PlayArrow', component: PlayArrowIcon },
  { name: 'Refresh', component: RefreshIcon },
  { name: 'RestartAlt', component: RestartAltIcon },
  { name: 'Save', component: SaveIcon },
  { name: 'Search', component: SearchIcon },
  { name: 'Settings', component: SettingsIcon },
  { name: 'Storage', component: StorageIcon },
  { name: 'Stop', component: StopIcon },
  { name: 'SyncAlt', component: SyncAltIcon },
  { name: 'Terminal', component: TerminalIcon },
  { name: 'Update', component: UpdateIcon },
  { name: 'Visibility', component: VisibilityIcon },
  { name: 'Warning', component: WarningIcon },
  { name: 'Wifi', component: WifiIcon },
];

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, label = 'Icon', helperText }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedIcon = AVAILABLE_ICONS.find((icon) => icon.name === value);
  const SelectedIconComponent = selectedIcon?.component;

  const filteredIcons = AVAILABLE_ICONS.filter((icon) => icon.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleIconSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <>
      <TextField
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        fullWidth
        InputProps={{
          startAdornment: SelectedIconComponent ? (
            <InputAdornment position="start">
              <SelectedIconComponent />
            </InputAdornment>
          ) : null,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setOpen(true)} size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Icon</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Search icons"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={1}>
            {filteredIcons.map((icon) => {
              const IconComponent = icon.component;
              return (
                <Grid item xs={2} sm={1.5} md={1} key={icon.name}>
                  <Tooltip title={icon.name}>
                    <IconButton
                      onClick={() => handleIconSelect(icon.name)}
                      sx={{
                        'border': value === icon.name ? '2px solid primary.main' : '1px solid grey.300',
                        'borderRadius': 1,
                        'width': '100%',
                        'aspectRatio': '1',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <IconComponent />
                    </IconButton>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
          {filteredIcons.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No icons found</Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
